import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../services/api.js';
import { API_ENDPOINTS } from '../config/api.config.js';
import { RoleContext, ROLES, PERMISSIONS, getRoleDisplayName, getRoleTheme, getRoleDefaultRoute, getAvailableModules } from './RoleContext.helpers.js';
import { createLogger } from '../utils/logger';

const logger = createLogger('role');

/**
 * Role Provider Component
 * 
 * Role provider for managing user roles and permissions with:
 * - Current role state management
 * - User permissions fetching
 * - Role configuration management
 * - Permission checking utilities
 * - Role-based routing
 * 
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} The role provider component
 */
export const RoleProvider = ({ children }) => {
  const [currentRole, setCurrentRole] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        try {
          const { data } = await api.get(API_ENDPOINTS.AUTH.ME);
          setUserPermissions(data.permissions || []);
          setCurrentRole(data.role || ROLES.CLIENT);
          localStorage.setItem('user', JSON.stringify(data));
        } catch (err) {
          if (err?.response?.status === 401) {
            localStorage.removeItem('wms_access_token');
            localStorage.removeItem('user');
            setUserPermissions([]);
            setCurrentRole(null);
          } else {
            logger.warn('Failed to fetch permissions from server:', err?.message || err);
            setUserPermissions([]);
          }
        }
      } catch (error) {
        logger.warn('Error fetching permissions:', error.message);
        setUserPermissions([]);
      }
    };
    
    const token = localStorage.getItem('wms_access_token');
    if (token) {
      fetchPermissions();
    }
  }, []);

  const [roleConfig, setRoleConfig] = useState({});


  // Update configuration when role changes
  useEffect(() => {
    if (currentRole) {
      // Role-specific configuration
      const config = {
        role: currentRole,
        displayName: getRoleDisplayName(currentRole),
        theme: getRoleTheme(currentRole),
        defaultRoute: getRoleDefaultRoute(currentRole),
        availableModules: getAvailableModules(currentRole)
      };
      
      setRoleConfig(config);
      
      // Save to localStorage
      localStorage.setItem('userRole', currentRole);
    }
  }, [currentRole]);

  // Function to switch role (memoized)
  const switchRole = useCallback((newRole) => {
    if (Object.values(ROLES).includes(newRole)) {
      setCurrentRole(newRole);
    }
  }, []);

  // Check if user has a permission (memoized)
  const hasPermission = useCallback((permission) => {
    return userPermissions.includes(permission);
  }, [userPermissions]);

  // Check if user has all required permissions (memoized)
  const hasAllPermissions = useCallback((permissions) => {
    return permissions.every(permission => hasPermission(permission));
  }, [hasPermission]);

  // Check if user has at least one of the permissions (memoized)
  const hasAnyPermission = useCallback((permissions) => {
    return permissions.some(permission => hasPermission(permission));
  }, [hasPermission]);

  // Check module access (memoized)
  const canAccessModule = useCallback((module) => {
    const modulePermissions = {
      'wms': [PERMISSIONS.WMS_VIEW],
      'tms': [PERMISSIONS.TMS_VIEW],
      'oms': [PERMISSIONS.OMS_VIEW],
      'manager': [PERMISSIONS.DASHBOARD_EXECUTIVE],
      'admin': [PERMISSIONS.SYSTEM_CONFIG]
    };
    
    return hasAnyPermission(modulePermissions[module] || []);
  }, [hasAnyPermission]);

  const value = useMemo(() => ({
    currentRole,
    userPermissions,
    roleConfig,
    switchRole,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    canAccessModule,
    ROLES,
    PERMISSIONS
  }), [
    currentRole,
    userPermissions,
    roleConfig,
    switchRole,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    canAccessModule
  ]);

  return (
    <RoleContext.Provider value={value}>
      {children}
    </RoleContext.Provider>
  );
};


export default RoleProvider;

